// 화면 그리기(§9.8.4) — 원격 화면 위 겹침 캔버스 + 도구 막대 + 글자 입력칸.
//
// 🔴 캔버스는 **영상의 실제 표시 영역**(테두리 안쪽, 비율 맞춤 후)에 정확히 겹친다. 영상 여백에 그리면 원격지 좌표 밖이다.
// 🔴 무대 격리(§9.6, `stageIsolation.ts`): 도구 막대·캔버스 위 조작이 무대의 "IME 칸으로 포커스 되돌리기"나
//    영상의 원격 입력으로 새지 않게 끊는다. 그리기 도구를 잡은 동안 캔버스가 영상 위를 덮어 원격 마우스 입력이 멈춘다.
import { useCallback, useEffect, useRef, useState } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent, PointerEvent as ReactPointerEvent, RefObject } from 'react';
import type { Drawing, RemoteState } from '../draw/useDrawing';
import { renderEditor, TEXT_FONT_FAMILY } from '../draw/render';
import { scalePx } from '../draw/geometry';
import type { Pt, Space } from '../draw/geometry';
import { COLORS, TEXT_SIZES, TOOLS, WIDTHS, toolCursor } from '../draw/style';
import type { Tool } from '../draw/style';
import { stageIsolationProps } from './stageIsolation';

/** 캔버스 자리(무대 기준 CSS px). */
interface Box {
  left: number;
  top: number;
  w: number;
  h: number;
}

/** 도구별 마우스 모양. */

/** 원격지 그림판 상태 표시(도구 막대). */
const REMOTE_LABEL: Record<RemoteState, string> = {
  'local-only': '내 화면에만',
  opening: '상대 화면 여는 중…',
  shared: '상대 화면에도 표시',
};
const REMOTE_HINT: Record<RemoteState, string> = {
  'local-only': '상대 기기에 그림판을 띄울 수 없습니다(앱이 없거나 이전 버전) — 내 화면에만 보이며 PNG 로 저장할 수 있습니다',
  opening: '상대 기기에 그림판을 띄우는 중입니다',
  shared: '그린 것이 상대 화면에도 똑같이 보입니다',
};

/**
 * 영상 요소 안에서 **실제 화면이 그려지는 사각형**(무대 기준). 테두리를 빼고, 요소가 영상보다 크면
 * (첫 프레임 직후·계산 전) 비율 맞춤 여백까지 뺀다 — 마우스 좌표 변환(`videoPoint`)과 같은 규칙.
 */
function contentBox(video: HTMLVideoElement, stage: HTMLElement): Box | null {
  const vw = video.videoWidth;
  const vh = video.videoHeight;
  const cw = video.clientWidth;
  const ch = video.clientHeight;
  if (!vw || !vh || !cw || !ch) return null;
  const vr = video.getBoundingClientRect();
  const sr = stage.getBoundingClientRect();
  const scale = Math.min(cw / vw, ch / vh);
  const w = vw * scale;
  const h = vh * scale;
  return {
    left: vr.left - sr.left + video.clientLeft + (cw - w) / 2,
    top: vr.top - sr.top + video.clientTop + (ch - h) / 2,
    w,
    h,
  };
}

export function DrawLayer({
  drawing,
  videoRef,
  stageRef,
  refocus,
}: {
  drawing: Drawing;
  videoRef: RefObject<HTMLVideoElement | null>;
  stageRef: RefObject<HTMLElement | null>;
  /** 원격 키 입력용 숨은 입력칸으로 포커스를 되돌린다. */
  refocus: () => void;
}) {
  const ed = drawing.editor;
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [box, setBox] = useState<Box | null>(null);
  const boxRef = useRef<Box | null>(null);
  boxRef.current = box;
  // 글자 입력 중인 자리(비율 좌표 + 화면 위치).
  const [textAt, setTextAt] = useState<{ at: Pt; x: number; y: number } | null>(null);
  const textRef = useRef<HTMLTextAreaElement>(null);
  // 글자 도구로 누른 자리 — 뗄 때 칸을 연다(onPointerDown 주석).
  const pendingText = useRef<Pt | null>(null);

  // ── 캔버스 자리 맞추기(무대 크기·영상 해상도·창 크기가 바뀔 때) ──
  useEffect(() => {
    const video = videoRef.current;
    const stage = stageRef.current;
    if (!drawing.active || !video || !stage) return;
    const fit = () => {
      const b = contentBox(video, stage);
      setBox((prev) =>
        prev && b && prev.left === b.left && prev.top === b.top && prev.w === b.w && prev.h === b.h ? prev : b,
      );
    };
    const ro = new ResizeObserver(fit);
    ro.observe(stage);
    ro.observe(video);
    video.addEventListener('resize', fit);
    window.addEventListener('resize', fit);
    fit();
    return () => {
      ro.disconnect();
      video.removeEventListener('resize', fit);
      window.removeEventListener('resize', fit);
    };
  }, [drawing.active, videoRef, stageRef]);

  // ── 다시 그리기(React 밖 — `useDrawing` 머리말) ──
  const hideRef = useRef(drawing.hideAuthor);
  hideRef.current = drawing.hideAuthor;
  const frame = useRef(0);
  const paint = useCallback(() => {
    frame.current = 0;
    const c = canvasRef.current;
    const b = boxRef.current;
    if (!c || !b) return;
    const dpr = window.devicePixelRatio || 1;
    const pw = Math.round(b.w * dpr);
    const ph = Math.round(b.h * dpr);
    if (c.width !== pw || c.height !== ph) {
      c.width = pw;
      c.height = ph;
    }
    const ctx = c.getContext('2d');
    if (!ctx) return;
    renderEditor(ctx, ed, { w: b.w, h: b.h }, dpr, { hideAuthor: hideRef.current });
    // 레이저 꼬리가 남아 있으면 사라질 때까지 계속 그린다.
    if (ed.pruneLasers()) frame.current = requestAnimationFrame(paint);
  }, [ed]);
  const schedule = useCallback(() => {
    if (!frame.current) frame.current = requestAnimationFrame(paint);
  }, [paint]);
  useEffect(() => drawing.subscribe(schedule), [drawing, schedule]);
  useEffect(() => {
    schedule();
  }, [box, drawing.active, drawing.hideAuthor, schedule]);
  useEffect(() => () => cancelAnimationFrame(frame.current), []);

  // 글자 입력칸이 열리면 포커스.
  useEffect(() => {
    if (textAt) textRef.current?.focus();
  }, [textAt]);
  // 도구를 바꾸면 쓰던 글자는 확정한다(버리지 않는다).
  useEffect(() => {
    if (drawing.tool !== 'text') commitText(); // commitText 는 입력칸의 최신 값을 ref 로 읽는다
  }, [drawing.tool]);

  if (!drawing.active) {
    // 원격지가 그리기를 끈 경우 — 왜 사라졌는지 한 번 알린다(누르면 닫힘).
    return drawing.notice ? (
      <div className="draw-notice" {...stageIsolationProps} onClick={(e) => {
        e.stopPropagation();
        drawing.clearNotice();
        refocus();
      }}>
        {drawing.notice}
      </div>
    ) : null;
  }

  const space = (): Space | null => (box ? { w: box.w, h: box.h } : null);
  const toPt = (e: ReactPointerEvent): Pt | null => {
    const c = canvasRef.current;
    if (!c || !box) return null;
    const r = c.getBoundingClientRect();
    return [(e.clientX - r.left) / r.width, (e.clientY - r.top) / r.height];
  };

  const onPointerDown = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    if (e.button !== 0) return;
    const p = toPt(e);
    const s = space();
    if (!p || !s) return;
    if (ed.tool === 'text') {
      // 🔴 글자 칸은 **뗄 때** 연다. 누를 때 열면 칸이 커서 바로 아래에 생겨 뗌이 칸 위에서 일어나고,
      //    브라우저는 click 을 두 요소의 공통 조상(무대)에 보낸다 → 무대가 포커스를 숨은 입력칸으로 가져가
      //    글자 칸이 열리자마자 닫혔다(2026-09-26 실측: pointerup=칸, click=stage).
      pendingText.current = p;
      return;
    }
    e.currentTarget.setPointerCapture(e.pointerId);
    ed.down(p, s);
  };
  const onPointerMove = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    const p = toPt(e);
    const s = space();
    if (p && s && ed.busy) ed.move(p, s);
  };
  const onPointerUp = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    const p = toPt(e);
    const s = space();
    if (p && s && ed.busy) ed.up(p, s);
    const at = pendingText.current;
    pendingText.current = null;
    if (ed.tool === 'text' && at && s) {
      commitText(); // 쓰던 칸이 있으면 먼저 확정
      setTextAt({ at, x: at[0] * s.w, y: at[1] * s.h });
      return;
    }
    if (ed.tool !== 'text') refocus(); // 단축키를 계속 받게(숨은 입력칸 → 세션 화면이 키를 여기로 넘긴다)
  };

  function commitText() {
    const el = textRef.current;
    if (el && textAt) ed.addText(textAt.at, el.value);
    setTextAt(null);
  }
  const onTextKey = (e: ReactKeyboardEvent<HTMLTextAreaElement>) => {
    e.stopPropagation(); // 무대·원격으로 새지 않게
    if (e.nativeEvent.isComposing) return; // 한글 조합 중 Enter 는 조합 확정이다
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      commitText();
      refocus();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setTextAt(null);
      refocus();
    }
  };

  const tool = drawing.tool;
  const textSize = box ? scalePx(TEXT_SIZES[ed.width] ?? TEXT_SIZES[6], { w: box.w, h: box.h }) : 16;
  const fillable = ed.fillable;
  const pick = (t: Tool) => () => {
    ed.setTool(t);
    refocus();
  };

  return (
    <>
      {box && (
        <canvas
          ref={canvasRef}
          className="draw-canvas"
          style={{
            left: box.left,
            top: box.top,
            width: box.w,
            height: box.h,
            // 조작 도구면 그림만 보이고 마우스는 아래 영상(원격 조작)으로 간다.
            pointerEvents: drawing.capturesInput ? 'auto' : 'none',
            cursor: toolCursor(tool),
          }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          // mousedown 기본 동작(포커스를 body 로 옮김)을 막는다 — 막지 않으면 단축키·원격 키가 끊긴다.
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onClick={(e) => e.stopPropagation()}
          onContextMenu={(e) => e.preventDefault()}
        />
      )}
      {box && textAt && (
        <textarea
          ref={textRef}
          className="draw-text-input"
          style={{
            left: box.left + textAt.x,
            top: box.top + textAt.y,
            fontSize: textSize,
            fontFamily: TEXT_FONT_FAMILY,
            color: ed.color,
          }}
          rows={1}
          placeholder="글자 입력 · Enter 확정 · Shift+Enter 줄바꿈"
          onKeyDown={onTextKey}
          onKeyUp={(e) => e.stopPropagation()}
          onBlur={commitText}
          {...stageIsolationProps}
        />
      )}
      <div className="draw-toolbar" {...stageIsolationProps} role="toolbar" aria-label="그리기 도구">
        {TOOLS.map((t) => (
          <button
            key={t.tool}
            className={`draw-btn${tool === t.tool ? ' on' : ''}`}
            title={`${t.label}${t.key ? ` (${t.key === 'escape' ? 'Esc' : t.key.toUpperCase()})` : ''}`}
            aria-pressed={tool === t.tool}
            onClick={pick(t.tool)}
          >
            {t.icon}
          </button>
        ))}
        <span className="draw-sep" />
        {COLORS.map((c) => (
          <button
            key={c.value}
            className={`draw-swatch${ed.color === c.value ? ' on' : ''}`}
            style={{ background: c.value }}
            title={c.label}
            aria-label={`색 ${c.label}`}
            onClick={() => {
              ed.setColor(c.value);
              refocus();
            }}
          />
        ))}
        <label className="draw-swatch custom" title="다른 색 고르기">
          <input type="color" value={ed.color} onChange={(e) => ed.setColor(e.target.value)} aria-label="다른 색" />
        </label>
        <span className="draw-sep" />
        {WIDTHS.map((w) => (
          <button
            key={w.value}
            className={`draw-btn${ed.width === w.value ? ' on' : ''}`}
            title={`굵기: ${w.label}`}
            onClick={() => {
              ed.setWidth(w.value);
              refocus();
            }}
          >
            <span className="draw-width-dot" style={{ width: w.value + 2, height: w.value + 2 }} />
          </button>
        ))}
        <button
          className={`draw-btn${ed.fill ? ' on' : ''}`}
          title="도형 채우기(사각형·원)"
          disabled={!fillable}
          onClick={() => {
            ed.setFill(!ed.fill);
            refocus();
          }}
        >
          ◼
        </button>
        <span className="draw-sep" />
        <button className="draw-btn" title="되돌리기 — 내가 한 것만 (Ctrl+Z)" disabled={!ed.canUndo} onClick={() => ed.undo()}>
          ↶
        </button>
        <button className="draw-btn" title="다시 실행 (Ctrl+Y)" disabled={!ed.canRedo} onClick={() => ed.redo()}>
          ↷
        </button>
        {tool === 'select' && (
          <>
            <button
              className="draw-btn"
              title="선택한 것 복제 (Ctrl+D)"
              disabled={!ed.selection.size}
              onClick={() => {
                const s = space();
                if (s) ed.duplicateSelection(s);
              }}
            >
              ⧉
            </button>
            <button className="draw-btn" title="선택한 것 삭제 (Delete)" disabled={!ed.selection.size} onClick={() => ed.deleteSelection()}>
              🗑
            </button>
          </>
        )}
        <button className="draw-btn" title="전체 지우기(되돌릴 수 있습니다)" disabled={ed.doc.visible().length === 0} onClick={() => ed.clear()}>
          🧹
        </button>
        <button
          className={`draw-btn${ed.doc.board ? ' on' : ''}`}
          title={ed.doc.board ? '흰 판 끄기 — 원격 화면으로 돌아가기' : '흰 판 — 화면을 흰 판으로 덮고 설명 그림 그리기'}
          onClick={() => {
            ed.setBoard(!ed.doc.board);
            refocus();
          }}
        >
          ⬜
        </button>
        <button
          className="draw-btn"
          title="PNG 저장 — 지금 화면과 그림을 원래 해상도 이미지로(흰 판이면 흰 판만)"
          onClick={() => {
            void drawing.savePng(videoRef.current);
            refocus();
          }}
        >
          💾
        </button>
        <span className="draw-sep" />
        {/* 상대에게 보이는가 — 보이지 않는데 보인다고 믿고 설명하는 일이 없게 늘 표시한다. */}
        <span className={`draw-status ${drawing.remote}`} title={REMOTE_HINT[drawing.remote]}>
          {drawing.peerBusy ? '✍ 상대가 그리는 중' : REMOTE_LABEL[drawing.remote]}
        </span>
        <button
          className="draw-btn close"
          title="그리기 끄기 — 그림이 모두 사라집니다(남기려면 먼저 💾 PNG 저장)"
          onClick={async () => {
            await drawing.requestOff(); // 그린 것이 있으면 확인 받고 끈다
            refocus();
          }}
        >
          ✕
        </button>
      </div>
    </>
  );
}
